import { OrganizationStatus } from '@/constants/organization'
import { backendApiFetch } from '@/hooks/backend-api-fetch'
import { fetchBackendApi } from '@/hooks/fetch-backend-api'
import { AddonPreviewItem } from '@/types/addons'
import { OrganizationPaddleSubscriptionDetailResponse } from '@/types/paddle-subscription'

/**
 * @deprecated Use OrganizationStatus instead
 */
export type SubscriptionStatus =
  | 'incomplete'
  | 'incomplete_expired'
  | 'trialing'
  | 'active'
  | 'deactivated'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'soft-locked'
  | 'hard-locked'
  | 'no-org'

/**
 * @deprecated Use OrganizationStatus instead
 */
interface CheckUserSubscriptionStatus {
  status?: SubscriptionStatus
}

/**
 * @deprecated Use OrganizationStatus instead
 */
interface CheckUserSubscriptionStatusParams {
  forceLatest?: boolean
}

interface CheckOrganizationSubscriptionStatus {
  status?: OrganizationStatus
}

interface CheckOrganizationSubscriptionStatusParams {
  forceLatest?: boolean
}

// eslint-disable-next-line no-unused-vars
export enum BillingProrationMode {
  ProratedImmediately = 'prorated_immediately',
  FullNextBillingPeriod = 'full_next_billing_period',
  FullImmediately = 'full_immediately',
}

export interface PreviewUpdatePaddleSubscriptionResponse {
  prorationBillingMode: BillingProrationMode
  updateSummary: Record<string, any> | null
  nextTransaction: {
    grandTotal: string
    currencyCode: string
    productName: string
  }
  basePlanSummary: {
    quantity: number
    unitTotals: {
      subtotal: string
      discount: string
      tax: string
      total: string
    }
    totals: {
      subtotal: string
      discount: string
      tax: string
      total: string
    }
    taxRate: number
  }
  addons: Array<AddonPreviewItem>
}

/**
 * Checks the subscription status of the current organization.
 *
 * @param {CheckOrganizationSubscriptionStatusParams} [params] - Optional parameters
 * @param {boolean} [params.forceLatest] - Force to check the latest subscription status
 * @returns {Promise<{
 *   data: CheckOrganizationSubscriptionStatus,
 *   ok: boolean,
 * }>}
 */
export const checkOrganizationSubscriptionStatusService = async ({
  forceLatest,
}: CheckOrganizationSubscriptionStatusParams = {}) => {
  const { data, ok } = await backendApiFetch<CheckOrganizationSubscriptionStatus>({
    endpoint: 'subscriptions/status',
    query: {
      forceLatest: !!forceLatest,
    },
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

interface UpdateBillingParams {
  priceId: string
  quantity: number
}

export async function updateBillingService(body: UpdateBillingParams) {
  const { data, ok } = await backendApiFetch({
    endpoint: 'subscriptions',
    method: 'PUT',
    isProtected: true,
    body,
  })
  return { data, ok }
}
export async function previewUpdateBillingService(body: UpdateBillingParams) {
  const { data, ok } = await backendApiFetch<PreviewUpdatePaddleSubscriptionResponse>({
    endpoint: 'subscriptions/preview-paddle',
    method: 'POST',
    isProtected: true,
    body,
  })
  return { data, ok }
}

interface GetSubscriptionDetailPayload {
  priceId: string
}

export const getUserSubscription = async ({ forceLatest }: CheckOrganizationSubscriptionStatusParams = {}) => {
  const { data, ok } = await fetchBackendApi<GetSubscriptionDetailPayload>({
    endpoint: 'subscriptions',
    query: {
      forceLatest: !!forceLatest,
    },
    isProtected: true,
  })

  return {
    data,
    ok,
  }
}

export async function getOrganizationPaddleSubscriptionDetail() {
  return await fetchBackendApi<OrganizationPaddleSubscriptionDetailResponse>({
    endpoint: 'subscriptions/paddle-subscription-detail',
    method: 'GET',
    isProtected: true,
  })
}

export async function verifyPaddleSubscripiton() {
  const { data, ok } = await backendApiFetch({
    endpoint: 'subscriptions/verify-paddle',
    isProtected: true,
  })
  return { data, ok }
}

export const getCustomerIdFromPaddle = async () => {
  const { data, ok } = await backendApiFetch<{ customerId: string }>({
    endpoint: 'subscriptions/customer-id',
    isProtected: true,
  })
  return { data, ok }
}
